<script>
import { useAlert } from 'dashboard/composables';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import InboxSalesforceAPI from 'dashboard/api/inboxSalesforce';

// Standard Case fields only — pre-chat details (terminal, SCAC, etc.) are included in Description.
const DEFAULT_FIELD_MAPPINGS = [
  { chatwoot_field: 'email', salesforce_field: 'SuppliedEmail' },
  { chatwoot_field: 'phone_number', salesforce_field: 'SuppliedPhone' },
  { chatwoot_field: 'name', salesforce_field: 'SuppliedName' },
  { chatwoot_field: 'faq', salesforce_field: 'Subject' },
  { chatwoot_field: 'conversation_description', salesforce_field: 'Description' },
];

export default {
  components: {
    SettingsFieldSection,
    SettingsToggleSection,
    NextInput,
    NextButton,
  },
  props: {
    inbox: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      enabled: false,
      domain: '',
      clientKey: '',
      clientSecret: '',
      fieldMappings: [...DEFAULT_FIELD_MAPPINGS],
    };
  },
  mounted() {
    this.fetchConfig();
  },
  methods: {
    async fetchConfig() {
      this.isLoading = true;
      try {
        const { data } = await InboxSalesforceAPI.getConfig(this.inbox.id);
        this.enabled = !!data.enabled;
        this.domain = data.domain || '';
        this.clientKey = data.client_key || '';
        this.clientSecret = data.client_secret || '';
        this.fieldMappings =
          data.field_mappings?.length
            ? data.field_mappings
            : [...DEFAULT_FIELD_MAPPINGS];
      } catch (error) {
        useAlert(error?.message || this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
      } finally {
        this.isLoading = false;
      }
    },
    async saveConfig() {
      this.isSaving = true;
      try {
        const payload = {
          enabled: this.enabled,
          domain: this.domain,
          client_key: this.clientKey,
          client_secret: this.clientSecret,
          field_mappings: this.fieldMappings,
        };
        const { data } = await InboxSalesforceAPI.updateConfig(
          this.inbox.id,
          payload
        );

        this.enabled = !!data.enabled;
        this.domain = data.domain || '';
        this.clientKey = data.client_key || '';
        this.clientSecret = data.client_secret || '';
        this.fieldMappings =
          data.field_mappings?.length
            ? data.field_mappings
            : [...DEFAULT_FIELD_MAPPINGS];

        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(
          error?.response?.data?.error ||
            error?.message ||
            this.$t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE')
        );
      } finally {
        this.isSaving = false;
      }
    },
    addMappingRow() {
      this.fieldMappings.push({ chatwoot_field: '', salesforce_field: '' });
    },
    removeMappingRow(index) {
      this.fieldMappings.splice(index, 1);
      if (!this.fieldMappings.length) {
        this.fieldMappings = [...DEFAULT_FIELD_MAPPINGS];
      }
    },
  },
};
</script>

<template>
  <div class="mx-6 max-w-4xl">
    <div v-if="isLoading" class="py-4 text-sm text-n-slate-11">
      {{ $t('INBOX_MGMT.SETTINGS_POPUP.LOADING') }}
    </div>
    <div v-else class="space-y-4">
      <SettingsToggleSection
        v-model="enabled"
        :header="$t('INBOX_MGMT.SETTINGS_POPUP.SALESFORCE.ENABLE_TITLE')"
        :description="$t('INBOX_MGMT.SETTINGS_POPUP.SALESFORCE.ENABLE_DESCRIPTION')"
      />

      <SettingsFieldSection
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.SALESFORCE.DOMAIN_LABEL')"
      >
        <NextInput
          v-model="domain"
          :disabled="!enabled"
          :placeholder="$t('INBOX_MGMT.SETTINGS_POPUP.SALESFORCE.DOMAIN_PLACEHOLDER')"
        />
      </SettingsFieldSection>

      <SettingsFieldSection
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.SALESFORCE.CLIENT_KEY_LABEL')"
      >
        <NextInput
          v-model="clientKey"
          :disabled="!enabled"
          :placeholder="$t('INBOX_MGMT.SETTINGS_POPUP.SALESFORCE.CLIENT_KEY_PLACEHOLDER')"
        />
      </SettingsFieldSection>

      <SettingsFieldSection
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.SALESFORCE.CLIENT_SECRET_LABEL')"
      >
        <NextInput
          v-model="clientSecret"
          :disabled="!enabled"
          type="password"
          :placeholder="
            $t('INBOX_MGMT.SETTINGS_POPUP.SALESFORCE.CLIENT_SECRET_PLACEHOLDER')
          "
        />
      </SettingsFieldSection>

      <SettingsFieldSection
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.SALESFORCE.FIELD_MAPPING_LABEL')"
        :help-text="
          $t('INBOX_MGMT.SETTINGS_POPUP.SALESFORCE.FIELD_MAPPING_HELP')
        "
      >
        <div class="space-y-2">
          <div
            v-for="(mapping, index) in fieldMappings"
            :key="index"
            class="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2 items-center"
          >
            <NextInput
              v-model="mapping.chatwoot_field"
              :disabled="!enabled"
              :placeholder="
                $t('INBOX_MGMT.SETTINGS_POPUP.SALESFORCE.CHATWOOT_FIELD_LABEL')
              "
            />
            <NextInput
              v-model="mapping.salesforce_field"
              :disabled="!enabled"
              :placeholder="
                $t('INBOX_MGMT.SETTINGS_POPUP.SALESFORCE.SALESFORCE_FIELD_LABEL')
              "
            />
            <NextButton
              icon="i-lucide-trash"
              slate
              sm
              :disabled="!enabled"
              @click="removeMappingRow(index)"
            />
          </div>
          <NextButton
            sm
            ghost
            :disabled="!enabled"
            :label="$t('INBOX_MGMT.SETTINGS_POPUP.SALESFORCE.ADD_MAPPING')"
            @click="addMappingRow"
          />
        </div>
      </SettingsFieldSection>

      <div class="flex justify-end pt-2">
        <NextButton
          :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
          :is-loading="isSaving"
          @click="saveConfig"
        />
      </div>
    </div>
  </div>
</template>
